/**
 * DragDropLayout — HTML5 drag-and-drop grid for cards and category panels.
 *
 *   - Module-level singleton drag state shared across nested DDL instances
 *   - Auto-scroll-on-drag-near-edge with self-watchdog disarm
 *   - FLIP animation for layout-change transitions
 *   - GridCell: draggable card or category cell with custom drag-image
 *   - RootGap: drop slots between root-level items adjacent to categories
 *   - InnerEdgeGap: top/bottom drop strips inside category panels
 */
import React, {
  useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import { Box } from '@mui/material';
import { CARD_HEIGHT } from '../../constants/card-layout';
import { innerGridDisplayWidth } from '../../utils/hospitalCategoryLayoutHelpers';

let globalDragState = null;
const dragSubs = new Set();

const setGlobalDragState = (next) => {
  globalDragState = next;
  dragSubs.forEach((fn) => fn(next));
};

const useGlobalDragState = () => {
  const [state, setState] = useState(globalDragState);
  useEffect(() => {
    dragSubs.add(setState);
    return () => { dragSubs.delete(setState); };
  }, []);
  return state;
};

const AUTOSCROLL_ZONE = 160;
const AUTOSCROLL_OVERSHOOT = 120;

let autoScrollRaf = null;
let autoScrollArmed = 0;
let autoScrollNode = null;
let lastAutoPointer = { x: 0, y: 0, valid: false };

let autoScrollAcc = 0;
let autoScrollLastTs = 0;

let lastDragoverTs = 0;
const AUTOSCROLL_STALE_MS = 250;

const isElScrollable = (el) => {
  if (!el) return false;
  const oy = getComputedStyle(el).overflowY;
  if (!(oy === 'auto' || oy === 'scroll' || oy === 'overlay')) return false;
  return el.scrollHeight > el.clientHeight + 2;
};

const isRootElement = (el) => el === document.body || el === document.documentElement;

const findScrollableAncestor = (el) => {
  let n = el;
  while (n) {
    if (isRootElement(n)) break;
    if (isElScrollable(n)) return n;
    n = n.parentElement;
  }
  const se = document.scrollingElement || document.documentElement;
  if (se && se.scrollHeight > se.clientHeight + 2) return se;
  return null;
};

const getViewportScrollY = () => {
  if (typeof window !== 'undefined' && typeof window.scrollY === 'number') return window.scrollY;
  return (document.scrollingElement || document.documentElement || document.body || { scrollTop: 0 }).scrollTop;
};
const getViewportMaxScroll = () => {
  const scroller = document.scrollingElement || document.documentElement || document.body;
  if (!scroller) return 0;
  
  const docMax = (document.documentElement?.scrollHeight || 0) - (document.documentElement?.clientHeight || 0);
  const bodyMax = (document.body?.scrollHeight || 0) - (document.body?.clientHeight || 0);
  return Math.max(0, docMax, bodyMax, scroller.scrollHeight - scroller.clientHeight);
};

const autoScrollTick = (now) => {
  autoScrollRaf = requestAnimationFrame(autoScrollTick);
  if (!autoScrollArmed || !autoScrollNode || !lastAutoPointer.valid) {
    autoScrollLastTs = 0;
    autoScrollAcc = 0;
    return;
  }

  const ts = typeof now === 'number' ? now : (typeof performance !== 'undefined' ? performance.now() : Date.now());

  if (lastDragoverTs > 0 && (ts - lastDragoverTs) > AUTOSCROLL_STALE_MS) {
    autoScrollArmed = 0;
    autoScrollNode = null;
    lastAutoPointer = { x: 0, y: 0, valid: false };
    autoScrollAcc = 0;
    autoScrollLastTs = 0;
    if (autoScrollRaf) {
      cancelAnimationFrame(autoScrollRaf);
      autoScrollRaf = null;
    }
    return;
  }

  const dt = autoScrollLastTs > 0 ? Math.min(0.05, Math.max(0, (ts - autoScrollLastTs) / 1000)) : 1 / 60;
  autoScrollLastTs = ts;

  const node = autoScrollNode;
  const isRoot = isRootElement(node) || node === document.scrollingElement;
  const rect = isRoot
    ? { top: 0, bottom: window.innerHeight }
    : node.getBoundingClientRect();
  const y = lastAutoPointer.y;

  const effTop = isRoot ? rect.top : Math.max(rect.top, 0);
  const effBottom = isRoot ? rect.bottom : Math.min(rect.bottom, window.innerHeight);

  if (y < effTop - AUTOSCROLL_OVERSHOOT || y > effBottom + AUTOSCROLL_OVERSHOOT) {
    autoScrollAcc = 0;
    return;
  }

  const upPen = Math.min(AUTOSCROLL_ZONE + AUTOSCROLL_OVERSHOOT,
    Math.max(0, (effTop + AUTOSCROLL_ZONE) - y));
  const downPen = Math.min(AUTOSCROLL_ZONE + AUTOSCROLL_OVERSHOOT,
    Math.max(0, y - (effBottom - AUTOSCROLL_ZONE)));

  const currentScroll = isRoot ? getViewportScrollY() : node.scrollTop;
  const maxScroll = isRoot ? getViewportMaxScroll() : Math.max(0, node.scrollHeight - node.clientHeight);

  let dir = 0;
  let pen = 0;
  if (upPen > 0 && currentScroll > 0) { dir = -1; pen = upPen; }
  else if (downPen > 0 && currentScroll < maxScroll - 0.5) { dir = 1; pen = downPen; }
  if (dir === 0) {
    autoScrollAcc = 0;
    return;
  }

  const norm = Math.min(1, pen / AUTOSCROLL_ZONE);
  const speedPerSec = 240 + norm * norm * 960;
  
  autoScrollAcc += (dir < 0 ? -1 : 1) * speedPerSec * dt;
  const step = autoScrollAcc >= 0 ? Math.floor(autoScrollAcc) : Math.ceil(autoScrollAcc);
  if (step === 0) return;
  autoScrollAcc -= step;

  if (isRoot) {
    
    if (typeof window.scrollBy === 'function') {
      window.scrollBy(0, step);
    } else {
      const sc = document.scrollingElement || document.documentElement;
      if (sc) sc.scrollTop = Math.max(0, sc.scrollTop + step);
    }
  } else {
    if (step < 0) {
      node.scrollTop = Math.max(0, node.scrollTop + step);
    } else {
      
      node.scrollTop = Math.min(maxScroll, node.scrollTop + step);
    }
  }
};

const armAutoScroll = (preferred, containerEl) => {
  autoScrollArmed += 1;
  const useable = preferred && !isRootElement(preferred) && isElScrollable(preferred);
  const node = useable ? preferred : findScrollableAncestor(containerEl);
  autoScrollNode = node;
  
  lastDragoverTs = typeof performance !== 'undefined' ? performance.now() : Date.now();
  if (!autoScrollRaf) autoScrollRaf = requestAnimationFrame(autoScrollTick);
};

const disarmAutoScroll = () => {
  autoScrollArmed = Math.max(0, autoScrollArmed - 1);
  if (autoScrollArmed === 0) {
    autoScrollNode = null;
    lastAutoPointer = { x: 0, y: 0, valid: false };
    autoScrollAcc = 0;
    autoScrollLastTs = 0;
    if (autoScrollRaf) {
      cancelAnimationFrame(autoScrollRaf);
      autoScrollRaf = null;
    }
  }
};

let lastDragPointer = { x: 0, y: 0, valid: false };

if (typeof window !== 'undefined') {
  
  if (window.__ddlDragoverListener) {
    try { window.removeEventListener('dragover', window.__ddlDragoverListener, true); } catch {  }
  }
  const listener = (e) => {
    lastDragPointer = { x: e.clientX, y: e.clientY, valid: true };
    lastDragoverTs = typeof performance !== 'undefined' ? performance.now() : Date.now();
    if (autoScrollArmed > 0) {
      lastAutoPointer = { x: e.clientX, y: e.clientY, valid: true };
    }
    
    if (globalDragState) {
      try {
        document.dispatchEvent(new MouseEvent('mousemove', {
          bubbles: true, cancelable: true, view: window,
          clientX: e.clientX, clientY: e.clientY,
          screenX: e.clientX, screenY: e.clientY,
          button: 0, buttons: 1,
        }));
      } catch {  }
    }
  };
  window.__ddlDragoverListener = listener;
  window.addEventListener('dragover', listener, true);
}

const FLIP_DURATION = 1500;
const FLIP_EASE = 'cubic-bezier(0.19, 1, 0.22, 1)';

const useFlip = (signature) => {
  const rectsRef = useRef(new Map());
  const nodesRef = useRef(new Map());
  const animsRef = useRef(new Map());
  const isFirstRunRef = useRef(true);

  const register = useCallback((id, node) => {
    if (node) nodesRef.current.set(id, node);
    else nodesRef.current.delete(id);
  }, []);

  const prevSigRef = useRef(null);

  useLayoutEffect(() => {
    const captureAll = () => {
      const next = new Map();
      nodesRef.current.forEach((node, id) => {
        if (node) next.set(id, node.getBoundingClientRect());
      });
      return next;
    };

    if (isFirstRunRef.current) {
      isFirstRunRef.current = false;
      rectsRef.current = captureAll();
      prevSigRef.current = signature;
      return;
    }

    const sigChanged = signature !== prevSigRef.current;

    if (!sigChanged) {
      
      if (animsRef.current.size === 0) {
        rectsRef.current = captureAll();
      }
      return;
    }

    const prev = rectsRef.current;
    const next = captureAll();
    next.forEach((r, id) => {
      const p = prev.get(id);
      if (!p) return;
      const dx = p.left - r.left;
      const dy = p.top - r.top;
      if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) return;

      const old = animsRef.current.get(id);
      if (old) { try { old.cancel(); } catch {  } }

      try {
        const node = nodesRef.current.get(id);
        if (!node) return;
        const anim = node.animate(
          [
            { transform: `translate(${dx}px, ${dy}px)` },
            { transform: 'translate(0, 0)' },
          ],
          { duration: FLIP_DURATION, easing: FLIP_EASE, fill: 'both' },
        );
        anim.onfinish = () => {
          if (animsRef.current.get(id) === anim) animsRef.current.delete(id);
        };
        animsRef.current.set(id, anim);
      } catch {  }
    });
    rectsRef.current = next;
    prevSigRef.current = signature;
  });

  return register;
};

let dragDropInstanceSeq = 0;
const useInstanceId = () => {
  const ref = useRef(null);
  if (ref.current == null) {
    dragDropInstanceSeq += 1;
    ref.current = `ddl-${dragDropInstanceSeq}`;
  }
  return ref.current;
};

const sortCardsByLayout = (cards, layout) => {
  if (!layout || layout.length === 0) return cards;
  const positionById = new Map();
  layout.forEach((l, i) => positionById.set(l.id, l.position ?? i));
  const out = [...cards];
  out.sort((a, b) => {
    const ap = positionById.has(a.id) ? positionById.get(a.id) : Number.POSITIVE_INFINITY;
    const bp = positionById.has(b.id) ? positionById.get(b.id) : Number.POSITIVE_INFINITY;
    return ap - bp;
  });
  return out;
};

const buildLayoutFromOrder = (orderedIds, baseLayout, cards) => {
  const baseById = new Map((baseLayout || []).map((l) => [l.id, l]));
  const cardById = new Map((cards || []).map((c) => [c.id, c]));
  return orderedIds.map((id, index) => {
    const base = baseById.get(id) || {};
    const card = cardById.get(id) || {};
    return {
      ...base,
      id,
      position: index,
      width: base.width ?? card.width ?? 4,
      type: base.type ?? card.type ?? 'card',
      gridH: base.gridH ?? card.gridH,
      title: base.title ?? card.title,
    };
  });
};

const GridCell = ({
  card,
  idx,
  isLayoutMode,
  disabled,
  isMobile,
  uniformCardHeight,
  hospitalRootLayoutMarker,
  cardWidthCols,
  registerFlip,
  containerId,
  containerCategoryId,
  globalDrag,
  onCardDragStart,
  onCardDragEnd,
  onCardDropOnCard,
  onCategoryDropOnCategory,
  unlinkDragActive,
  generalDropHighlight,
  draggableCancel,
  children,
}) => {
  const isCategory = card.type === 'category';
  const cellRef = useRef(null);
  const [isHoveredAsTarget, setIsHoveredAsTarget] = useState(false);

  const isDragging = globalDrag?.cardId === card.id;

  const setRef = useCallback((node) => {
    cellRef.current = node;
    registerFlip(card.id, node);
  }, [card.id, registerFlip]);

  const handleDragStart = (e) => {
    if (!isLayoutMode || disabled) {
      e.preventDefault();
      return;
    }
    if (draggableCancel) {
      const t = e.target;
      if (t && typeof t.closest === 'function' && t.closest(draggableCancel)) {
        e.preventDefault();
        return;
      }
    }
    
    e.stopPropagation();
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', card.id); } catch {  }

    const sourceNode = cellRef.current;
    if (sourceNode && typeof e.dataTransfer.setDragImage === 'function') {
      try {
        const rect = sourceNode.getBoundingClientRect();
        const naturalW = sourceNode.offsetWidth || rect.width;
        const naturalH = sourceNode.offsetHeight || rect.height;
        const scale = naturalW > 0 ? rect.width / naturalW : 1;
        const visualW = rect.width;
        const visualH = rect.height;
        const offsetX = Math.max(0, e.clientX - rect.left);
        const offsetY = Math.max(0, e.clientY - rect.top);

        const wrapper = document.createElement('div');
        wrapper.style.position = 'fixed';
        wrapper.style.top = '-10000px';
        wrapper.style.left = '-10000px';
        wrapper.style.width = `${visualW}px`;
        wrapper.style.height = `${visualH}px`;
        wrapper.style.overflow = 'hidden';
        wrapper.style.pointerEvents = 'none';
        wrapper.style.background = 'transparent';

        const clone = sourceNode.cloneNode(true);
        clone.style.position = 'absolute';
        clone.style.top = '0';
        clone.style.left = '0';
        clone.style.width = `${naturalW}px`;
        clone.style.height = `${naturalH}px`;
        clone.style.transform = `scale(${scale})`;
        clone.style.transformOrigin = 'top left';
        clone.style.margin = '0';
        clone.style.pointerEvents = 'none';

        const stripFx = (n) => {
          n.style.setProperty('box-shadow', 'none', 'important');
          n.style.setProperty('filter', 'none', 'important');
          n.style.setProperty('-webkit-filter', 'none', 'important');
        };
        stripFx(clone);
        clone.querySelectorAll('*').forEach(stripFx);

        wrapper.appendChild(clone);
        document.body.appendChild(wrapper);
        e.dataTransfer.setDragImage(wrapper, offsetX, offsetY);

        setTimeout(() => { try { wrapper.remove(); } catch {  } }, 0);
      } catch {  }
    }

    setGlobalDragState({
      cardId: card.id,
      cardType: isCategory ? 'category' : 'card',
      sourceContainerId: containerId,
      sourceCategoryId: containerCategoryId || null,
    });
    onCardDragStart?.(e, card);
  };

  const handleDragEnd = (e) => {
    e.stopPropagation();
    setIsHoveredAsTarget(false);
    onCardDragEnd?.(e, card);
    setGlobalDragState(null);
  };

  const handleDragOver = (e) => {
    if (!globalDrag) return;
    if (isCategory && globalDrag.cardType !== 'category') return;
    if (!isCategory && globalDrag.cardType === 'category'
      && globalDrag.sourceContainerId !== containerId) return;
    if (globalDrag.cardId === card.id) {
      
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'move';
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setIsHoveredAsTarget(true);
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setIsHoveredAsTarget(false);
  };

  const handleDrop = (e) => {
    if (!globalDrag) return;
    if (isCategory && globalDrag.cardType !== 'category') return;
    if (!isCategory && globalDrag.cardType === 'category'
      && globalDrag.sourceContainerId !== containerId) return;
    if (globalDrag.cardId === card.id) {
      
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    setIsHoveredAsTarget(false);
    
    if (globalDrag.cardType === 'category') {
      onCategoryDropOnCategory?.(globalDrag.cardId, card.id, null, e);
    } else {
      onCardDropOnCard?.(globalDrag, card, e);
    }
  };

  const innerSx = useMemo(() => {
    let transform = 'scale(1)';
    if (isHoveredAsTarget) transform = isCategory ? 'scale(0.995)' : 'scale(0.98)';

    let boxShadow = 'none';
    if (isHoveredAsTarget) {
      boxShadow = '0 14px 30px -8px rgba(76, 118, 213, 0.45), 0 4px 8px -2px rgba(76, 118, 213, 0.25)';
    }

    return {
      position: 'relative',
      height: '100%',
      width: '100%',
      boxSizing: 'border-box',
      transition: 'transform 220ms cubic-bezier(0.32, 0.72, 0, 1), box-shadow 220ms cubic-bezier(0.32, 0.72, 0, 1)',
      transform,
      boxShadow,
      opacity: isDragging ? 0.85 : 1,
      
      zIndex: isDragging ? 5 : 'auto',
      cursor: isLayoutMode && !disabled ? (isDragging ? 'grabbing' : 'grab') : 'default',
      overflow: isLayoutMode && !isCategory ? 'hidden' : 'visible',
      borderRadius: isCategory ? '14px' : '12px',
      ...(unlinkDragActive && isDragging ? {
        outline: '4px solid rgba(76, 118, 213, 0.7)',
        outlineOffset: '2px',
      } : {}),
    };
  }, [isDragging, isHoveredAsTarget, isCategory, isLayoutMode, disabled, unlinkDragActive]);

  const cellHeight = isCategory && !uniformCardHeight ? '100%' : `${CARD_HEIGHT}px`;

  return (
    <Box
      ref={setRef}
      data-rgl-item-id={card.id}
      {...(hospitalRootLayoutMarker && !isCategory ? { 'data-hospital-root-card-id': card.id } : {})}
      sx={{
        gridColumn: isMobile ? 'span 12' : `span ${cardWidthCols}`,
        gridRow: 'span 1',
        height: isMobile ? 'auto' : cellHeight,
        position: 'relative',
        
        zIndex: isDragging ? 5 : 'auto',
        ...(generalDropHighlight && hospitalRootLayoutMarker && !isCategory ? {
          outline: '2px dashed rgba(25,118,210,0.6)',
          outlineOffset: '-2px',
          borderRadius: '10px',
          backgroundColor: 'rgba(25,118,210,0.05)',
        } : {}),
      }}
    >
      <Box
        draggable={isLayoutMode && !disabled}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={innerSx}
      >
        
        {isHoveredAsTarget && (
          <Box sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: 'inherit',
            outline: '2px solid rgba(76, 118, 213, 0.7)',
            backgroundColor: 'rgba(76, 118, 213, 0.08)',
            pointerEvents: 'none',
            zIndex: 5,
          }} />
        )}
        {children(card, idx, isLayoutMode)}
      </Box>
    </Box>
  );
};

const RootGap = ({ index, isLayoutMode, onRootGapDrop }) => {
  const globalDrag = useGlobalDragState();
  const [hover, setHover] = useState(false);

  const isCategoryDrag = globalDrag?.cardType === 'category';
  const accept = isLayoutMode && (
    globalDrag?.cardType === 'card' || isCategoryDrag
  );

  if (!accept) {
    return <Box sx={{ gridColumn: 'span 12', height: 0, p: 0, m: 0 }} />;
  }

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    if (!hover) setHover(true);
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setHover(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setHover(false);
    const cardId = globalDrag?.cardId;
    if (cardId != null) onRootGapDrop?.(cardId, index, e, globalDrag?.cardType);

    setGlobalDragState(null);
  };

  return (
    <Box
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={{
        gridColumn: 'span 12',
        height: hover ? '82px' : '34px',
        transition: 'height 220ms cubic-bezier(0.32, 0.72, 0, 1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        zIndex: 4,
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '100%',
          borderRadius: '10px',
          border: '2px dashed',
          borderColor: hover
            ? 'rgba(76, 118, 213, 0.85)'
            : 'rgba(76, 118, 213, 0.4)',
          backgroundColor: hover
            ? 'rgba(76, 118, 213, 0.08)'
            : 'rgba(76, 118, 213, 0.03)',
          color: 'rgba(76, 118, 213, 0.95)',
          fontSize: '1rem',
          fontWeight: 600,
          letterSpacing: '0.3px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition:
            'border-color 220ms ease, background-color 220ms ease, opacity 180ms ease',
          opacity: hover ? 1 : 0.7,
          
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      >
        {hover ? (isCategoryDrag ? 'Drop category here' : 'Drop here as a root-level card') : ''}
      </Box>
    </Box>
  );
};

const InnerEdgeGap = ({
  position,
  isLayoutMode,
  containerId,
  containerCategoryId,
  onCrossContainerEdgeDrop,
}) => {
  const globalDrag = useGlobalDragState();
  const [hover, setHover] = useState(false);

  const accept =
    isLayoutMode &&
    globalDrag?.cardType === 'card' &&
    globalDrag?.sourceContainerId !== containerId;

  if (!accept) {
    return <Box sx={{ gridColumn: 'span 12', height: 0, p: 0, m: 0 }} />;
  }

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    if (!hover) setHover(true);
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setHover(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setHover(false);
    const cardId = globalDrag?.cardId;
    if (!cardId) {
      setGlobalDragState(null);
      return;
    }
    onCrossContainerEdgeDrop?.(cardId, position, containerCategoryId);

    setGlobalDragState(null);
  };

  return (
    <Box
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={{
        gridColumn: 'span 12',
        height: hover ? '46px' : '28px',
        transition: 'height 200ms cubic-bezier(0.32, 0.72, 0, 1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        zIndex: 4,
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '100%',
          borderRadius: '8px',
          border: '2px dashed',
          borderColor: hover
            ? 'rgba(76, 118, 213, 0.85)'
            : 'rgba(76, 118, 213, 0.4)',
          backgroundColor: hover
            ? 'rgba(76, 118, 213, 0.08)'
            : 'rgba(76, 118, 213, 0.03)',
          color: 'rgba(76, 118, 213, 0.95)',
          fontSize: '0.74rem',
          fontWeight: 600,
          letterSpacing: '0.2px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition:
            'border-color 200ms ease, background-color 200ms ease, opacity 160ms ease',
          opacity: hover ? 1 : 0.65,
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      >
        {hover ? (position === 'start' ? 'Drop at start' : 'Drop at end') : ''}
      </Box>
    </Box>
  );
};

const DragDropLayout = ({
  cards,
  layout,
  onLayoutChange,
  children,
  isLayoutMode = false,
  disabled = false,
  isMobile = false,
  onDragStart,
  onDragStop,
  unlinkDragActive = false,
  uniformCardHeight = true,
  disableContainerGutter = false,
  innerTwoColumnDesktop = false,
  draggableCancel = '',
  shouldPersistLayoutAfterDrag,
  hospitalRootLayoutMarker = false,
  generalDropHighlight = false,
  layoutScrollContainerRef = null,
  
  containerCategoryId = null,
  
  enableRootGaps = false,
  onRootGapDrop,
  
  enableInnerEdgeGaps = false,
  onCrossContainerEdgeDrop,
  ...legacyCompatProps
}) => {
  void legacyCompatProps;
  const containerId = useInstanceId();
  const containerRef = useRef(null);
  const globalDrag = useGlobalDragState();

  const innerCompactTwoCol = Boolean(disableContainerGutter && innerTwoColumnDesktop);

  const visibleCards = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const c of (cards || [])) {
      if (!c) continue;
      if (seen.has(c.id)) continue;
      if (!(c.type === 'category' || c.component)) continue;
      seen.add(c.id);
      out.push(c);
    }
    return out;
  }, [cards]);

  const orderedCards = useMemo(
    () => sortCardsByLayout(visibleCards, layout),
    [visibleCards, layout],
  );

  const widthForCard = useCallback((card) => {
    if (card.type === 'category') return 12;
    const raw = card.width || 4;
    return innerGridDisplayWidth(raw, innerCompactTwoCol);
  }, [innerCompactTwoCol]);

  const layoutSignature = useMemo(
    () => orderedCards.map((c) => `${c.id}:${widthForCard(c)}:${c.gridH ?? ''}`).join('|'),
    [orderedCards, widthForCard],
  );
  const registerFlip = useFlip(layoutSignature);

  const handleCardDragStart = useCallback((e, card) => {
    armAutoScroll(layoutScrollContainerRef?.current, containerRef.current);
    onDragStart?.(layout, null, { i: card.id, w: widthForCard(card), h: card.gridH }, null, e, null);
  }, [onDragStart, layout, widthForCard, layoutScrollContainerRef]);

  const handleCardDragEnd = useCallback((e, card) => {
    
    const captured = lastDragPointer.valid
      ? { x: lastDragPointer.x, y: lastDragPointer.y }
      : null;
    disarmAutoScroll();

    let evt = e;
    if (captured) {
      evt = {
        clientX: captured.x,
        clientY: captured.y,
        target: e?.target,
        currentTarget: e?.currentTarget,
        preventDefault: () => {},
        stopPropagation: () => {},
      };
    }

    onDragStop?.(layout, null, { i: card.id, w: widthForCard(card), h: card.gridH }, null, evt, null);
  }, [onDragStop, layout, widthForCard]);

  const handleCardDropOnCard = useCallback((src, target) => {
    if (src.sourceContainerId !== containerId) return;
    if (!onLayoutChange || disabled) return;
    if (shouldPersistLayoutAfterDrag && !shouldPersistLayoutAfterDrag()) return;

    const ids = orderedCards.map((c) => c.id);
    const fromIdx = ids.indexOf(src.cardId);
    const toIdx = ids.indexOf(target.id);
    if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return;

    const swapById = () => {
      const next = [...ids];
      [next[fromIdx], next[toIdx]] = [next[toIdx], next[fromIdx]];
      onLayoutChange(buildLayoutFromOrder(next, layout, visibleCards));
    };

    const cardById = new Map(orderedCards.map((c) => [c.id, c]));
    const srcWidth = widthForCard(orderedCards[fromIdx]);
    const targetWidth = widthForCard(cardById.get(target.id) ?? orderedCards[toIdx]);

    let wideId;
    let otherId;
    let wideWidth;
    if (srcWidth >= 8) {
      wideId = src.cardId;
      otherId = target.id;
      wideWidth = srcWidth;
    } else if (targetWidth >= 8) {
      wideId = target.id;
      otherId = src.cardId;
      wideWidth = targetWidth;
    } else {
      swapById();
      return;
    }

    const container = containerRef.current;
    if (!container) {
      swapById();
      return;
    }

    const items = [];
    for (const child of Array.from(container.children)) {
      const id = child.getAttribute?.('data-rgl-item-id');
      if (!id) continue;
      const r = child.getBoundingClientRect();
      items.push({ id, top: r.top, left: r.left });
    }
    items.sort((a, b) => a.top - b.top || a.left - b.left);

    const rowTolerance = 20;
    const rows = [];
    let curRow = [];
    let curTop = null;
    for (const it of items) {
      if (curTop === null || Math.abs(it.top - curTop) <= rowTolerance) {
        curRow.push(it.id);
        if (curTop === null) curTop = it.top;
      } else {
        rows.push(curRow);
        curRow = [it.id];
        curTop = it.top;
      }
    }
    if (curRow.length > 0) rows.push(curRow);

    const wideRowIdx = rows.findIndex((r) => r.includes(wideId));
    const destRowIdx = rows.findIndex((r) => r.includes(otherId));
    if (wideRowIdx === -1 || destRowIdx === -1 || wideRowIdx === destRowIdx) {
      swapById();
      return;
    }

    const wideRow = rows[wideRowIdx];
    const destRow = rows[destRowIdx];
    const wideIdxInWideRow = wideRow.indexOf(wideId);

    const colsInRow = (row) => {
      const colOf = new Map();
      const widthOf = new Map();
      let col = 0;
      for (const id of row) {
        const c = cardById.get(id);
        const w = c ? widthForCard(c) : 4;
        colOf.set(id, col);
        widthOf.set(id, w);
        col += w;
      }
      return { colOf, widthOf };
    };

    const wideCols = colsInRow(wideRow);
    const destCols = colsInRow(destRow);
    const wideOrigCol = wideCols.colOf.get(wideId) ?? 0;
    const smallCol = destCols.colOf.get(otherId) ?? 0;

    const validStarts = [0];
    let cumCol = 0;
    for (const id of destRow) {
      cumCol += destCols.widthOf.get(id) ?? 4;
      if (cumCol > 0 && cumCol + wideWidth <= 12) validStarts.push(cumCol);
    }
    const includesSmall = validStarts.filter(
      (s) => smallCol >= s && smallCol < s + wideWidth,
    );
    const candidates = includesSmall.length > 0 ? includesSmall : validStarts;
    const dStart = candidates.reduce((best, s) =>
      Math.abs(s - wideOrigCol) < Math.abs(best - wideOrigCol) ? s : best
    );
    const dEnd = dStart + wideWidth;

    const stayingBefore = [];
    const stayingAfter = [];
    const displaced = [];
    for (const id of destRow) {
      const cStart = destCols.colOf.get(id);
      const cEnd = cStart + destCols.widthOf.get(id);
      const overlaps = cStart < dEnd && cEnd > dStart;
      if (overlaps) displaced.push(id);
      else if (cEnd <= dStart) stayingBefore.push(id);
      else stayingAfter.push(id);
    }

    const wideRowBefore = wideRow.slice(0, wideIdxInWideRow);
    const wideRowAfter = wideRow.slice(wideIdxInWideRow + 1);
    const fullSwap =
      displaced.length > 0 && stayingBefore.length === 0 && stayingAfter.length === 0;
    const newDestRow = fullSwap
      ? [...wideRow]
      : [...stayingBefore, wideId, ...stayingAfter];
    const newWideRow = fullSwap
      ? [...displaced]
      : [...wideRowBefore, ...displaced, ...wideRowAfter];

    const newIds = [];
    for (let i = 0; i < rows.length; i++) {
      if (i === destRowIdx) newIds.push(...newDestRow);
      else if (i === wideRowIdx) newIds.push(...newWideRow);
      else newIds.push(...rows[i]);
    }

    if (newIds.length !== ids.length) {
      swapById();
      return;
    }

    const originalIds = orderedCards.map((c) => c.id);
    const unchanged = newIds.every((id, i) => id === originalIds[i]);
    if (unchanged) return;
    onLayoutChange(buildLayoutFromOrder(newIds, layout, visibleCards));
  }, [containerId, onLayoutChange, disabled, shouldPersistLayoutAfterDrag, orderedCards, layout, visibleCards, widthForCard]);

  const handleCategoryDropOnCategory = useCallback((draggedId, targetId ) => {
    if (!onLayoutChange || disabled) return;
    if (shouldPersistLayoutAfterDrag && !shouldPersistLayoutAfterDrag()) return;
    if (draggedId === targetId) return;

    const ids = orderedCards.map((c) => c.id);
    const fromIdx = ids.indexOf(draggedId);
    const toIdx = ids.indexOf(targetId);
    if (fromIdx === -1 || toIdx === -1) return;

    const next = [...ids];
    [next[fromIdx], next[toIdx]] = [next[toIdx], next[fromIdx]];
    onLayoutChange(buildLayoutFromOrder(next, layout, visibleCards));
  }, [onLayoutChange, disabled, shouldPersistLayoutAfterDrag, orderedCards, layout, visibleCards]);

  const handleAreaDragOver = (e) => {
    if (!globalDrag) return;
    if (globalDrag.cardType !== 'card') return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleAreaDragLeave = () => {};

  const findRootInsertIndexFromY = useCallback((clientY, excludeId) => {
    const container = containerRef.current;
    if (!container || typeof clientY !== 'number') return -1;
    let bestIdx = -1;
    let bestTop = Infinity;
    for (const child of Array.from(container.children)) {
      const id = child.getAttribute?.('data-rgl-item-id');
      if (!id || id === excludeId) continue;
      const r = child.getBoundingClientRect();
      if (r.top >= clientY && r.top < bestTop) {
        bestTop = r.top;
        bestIdx = orderedCards.findIndex((c) => c.id === id);
      }
    }
    return bestIdx;
  }, [orderedCards]);

  const handleAreaDrop = (e) => {
    if (!globalDrag || globalDrag.cardType !== 'card') return;
    if (e.defaultPrevented) return;
    e.preventDefault();
    if (globalDrag.sourceContainerId !== containerId) return;
    if (!onLayoutChange || disabled) return;
    if (shouldPersistLayoutAfterDrag && !shouldPersistLayoutAfterDrag()) return;

    const ids = orderedCards.map((c) => c.id);
    const fromIdx = ids.indexOf(globalDrag.cardId);
    if (fromIdx === -1) return;
    ids.splice(fromIdx, 1);

    const dropY = (e.clientY && typeof e.clientY === 'number' && e.clientY > 0)
      ? e.clientY
      : (lastDragPointer.valid ? lastDragPointer.y : null);
    let insertAt = ids.length;
    if (dropY != null) {
      const idx = findRootInsertIndexFromY(dropY, globalDrag.cardId);
      if (idx !== -1) {
        
        insertAt = idx > fromIdx ? idx - 1 : idx;
      }
    }
    ids.splice(insertAt, 0, globalDrag.cardId);
    
    const originalIds = orderedCards.map((c) => c.id);
    const unchanged =
      ids.length === originalIds.length
      && ids.every((id, i) => id === originalIds[i]);
    if (unchanged) return;
    onLayoutChange(buildLayoutFromOrder(ids, layout, visibleCards));
  };

  const cols = 12;

  return (
    <Box
      ref={containerRef}
      {...(hospitalRootLayoutMarker ? { 'data-hospital-root-layout': '1' } : {})}
      
      className={disableContainerGutter ? 'hospital-category-inner-dnd' : undefined}
      onDragOver={handleAreaDragOver}
      onDragLeave={handleAreaDragLeave}
      onDrop={handleAreaDrop}
      sx={{
        width: '100%',
        boxSizing: 'border-box',
        ...(disableContainerGutter ? { ml: 0, mt: 0 } : {
          ml: { xs: 1, sm: 2 },
          mt: { xs: 1, sm: 2 },
          '@media (max-width: 768px)': { ml: 1, mt: 1 },
          '@media (max-width: 480px)': { ml: 0.5, mt: 0.5 },
        }),
        display: 'grid',
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gap: 1.5,
        rowGap: 1.5,
        gridAutoFlow: 'row',
        gridAutoRows: 'min-content',
      }}
    >
      
      {orderedCards.flatMap((card, idx) => {
        const cells = [];
        if (enableRootGaps) {
          const prev = idx > 0 ? orderedCards[idx - 1] : null;
          const isLeadingSlot = idx === 0;
          const isAdjacentToCategory =
            card.type === 'category' || (prev && prev.type === 'category');
          if (isLeadingSlot || isAdjacentToCategory) {
            cells.push(
              <RootGap
                key={`root-gap-${idx}`}
                index={idx}
                isLayoutMode={isLayoutMode}
                onRootGapDrop={onRootGapDrop}
              />
            );
          }
        }
        cells.push(
          <GridCell
            key={card.id}
            card={card}
            idx={idx}
            isLayoutMode={isLayoutMode}
            disabled={disabled}
            isMobile={isMobile}
            uniformCardHeight={uniformCardHeight}
            hospitalRootLayoutMarker={hospitalRootLayoutMarker}
            cardWidthCols={widthForCard(card)}
            registerFlip={registerFlip}
            containerId={containerId}
            containerCategoryId={containerCategoryId}
            globalDrag={globalDrag}
            onCardDragStart={handleCardDragStart}
            onCardDragEnd={handleCardDragEnd}
            onCardDropOnCard={handleCardDropOnCard}
            onCategoryDropOnCategory={handleCategoryDropOnCategory}
            unlinkDragActive={unlinkDragActive}
            generalDropHighlight={generalDropHighlight}
            draggableCancel={draggableCancel}
          >
            {children}
          </GridCell>
        );
        
        if (enableRootGaps && idx === orderedCards.length - 1) {
          cells.push(
            <RootGap
              key={`root-gap-trailing`}
              index={orderedCards.length}
              isLayoutMode={isLayoutMode}
              onRootGapDrop={onRootGapDrop}
            />
          );
        }
        return cells;
      })}
      {enableInnerEdgeGaps && orderedCards.length > 0 && (
        <InnerEdgeGap
          key="inner-edge-gap-end"
          position="end"
          isLayoutMode={isLayoutMode}
          containerId={containerId}
          containerCategoryId={containerCategoryId}
          onCrossContainerEdgeDrop={onCrossContainerEdgeDrop}
        />
      )}
    </Box>
  );
};

export default DragDropLayout;